// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register once — scoped, no global defaults mutation
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

// ── Shared colour palette ──────────────────────────────────────────
const PALETTE = [
  '#4dc9f6',
  '#f67019',
  '#f53794',
  '#537bc4',
  '#acc236',
  '#166a8f',
  '#00a950',
  '#58595b',
  '#8549ba',
];

export const chartColor = (i) => PALETTE[i % PALETTE.length];
export const chartColorAlpha = (i, a = 0.8) => {
  const hex = PALETTE[i % PALETTE.length];
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// ── Shared chart options helpers ───────────────────────────────────
export const defaultLineOptions = (title = '') => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { usePointStyle: true, padding: 20 } },
    title: {
      display: !!title,
      text: title,
      color: '#374151',
      font: { size: 14, weight: 'bold' },
    },
    tooltip: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      cornerRadius: 8,
      callbacks: {
        label: (ctx) => `₹${ctx.parsed.y.toLocaleString('en-IN')}`,
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#6B7280' } },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.06)' },
      ticks: {
        color: '#6B7280',
        callback: (v) => '₹' + v.toLocaleString('en-IN'),
      },
    },
  },
  interaction: { intersect: false, mode: 'index' },
});

export const defaultDoughnutOptions = (title = '') => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'right' },
    title: { display: !!title, text: title, color: '#374151' },
    tooltip: {
      callbacks: {
        label: (ctx) => `${ctx.label}: ₹${ctx.parsed.toLocaleString('en-IN')}`,
      },
    },
  },
});

// Legacy export — used by any file that still imports createLineChartData
// Returns data/options objects, not JSX
export function createLineChartData({ labels, values, label = 'Expenses' }) {
  return {
    data: {
      labels,
      datasets: [
        {
          label,
          data: values,
          backgroundColor: chartColorAlpha(0, 0.1),
          borderColor: chartColor(0),
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointBackgroundColor: chartColor(0),
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    },
    options: defaultLineOptions(label),
    type: 'line',
  };
}
